export interface PhysicalHandLayoutInput {
  readonly index: number;
  readonly count: number;
  readonly selectedIndex: number | null;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly collapsed: boolean;
}

export interface PhysicalHandCardTransform {
  readonly position: readonly [number, number, number];
  readonly rotation: readonly [number, number, number];
  readonly scale: number;
  readonly renderOrder: number;
}

export interface CameraPlanePointInput {
  readonly clientX: number;
  readonly clientY: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly distance: number;
  readonly fovDegrees: number;
}

export function physicalHandCameraDistance(viewportWidth: number, viewportHeight: number): number {
  return viewportHeight <= viewportWidth ? 5.9 : 6.2;
}

export function physicalHandBaseScale(viewportWidth: number, viewportHeight: number): number {
  if (viewportHeight <= viewportWidth) return viewportWidth >= 900 ? 0.58 : 0.36;
  if (viewportWidth <= 320) return 0.34;
  return 0.415;
}

export function getPhysicalHandCardTransform({
  index,
  count,
  selectedIndex,
  viewportWidth,
  viewportHeight,
  collapsed,
}: PhysicalHandLayoutInput): PhysicalHandCardTransform {
  const safeCount = Math.max(1, count);
  const center = (safeCount - 1) / 2;
  const offset = index - center;
  const normalized = center === 0 ? 0 : offset / center;
  const baseScale = physicalHandBaseScale(viewportWidth, viewportHeight);
  const cardWidth = 1.22 * baseScale;
  const maximumWidth = viewportHeight <= viewportWidth ? 4.7 : viewportWidth <= 320 ? 2.35 : 2.72;
  const preferredSpacing = cardWidth * 0.67;
  const spacing =
    safeCount <= 1
      ? 0
      : Math.max(
          cardWidth * 0.38,
          Math.min(preferredSpacing, (maximumWidth - cardWidth) / center / 2),
        );
  const selectedOffset = selectedIndex === null ? 0 : index - selectedIndex;
  const neighbourSpread =
    selectedIndex === null || selectedOffset === 0 ? 0 : Math.sign(selectedOffset) * spacing * 0.14;
  const landscape = viewportHeight <= viewportWidth;
  const selected = selectedIndex === index;
  const selectedEdgeInset = selected && !landscape ? -normalized * 0.36 : 0;
  const distance = physicalHandCameraDistance(viewportWidth, viewportHeight);
  const restingY = landscape ? -1.42 : -1.64;
  const outerDrop = Math.abs(normalized) * (landscape ? 0.08 : 0.11);

  return {
    position: [
      offset * spacing + neighbourSpread + selectedEdgeInset,
      collapsed ? restingY - 1.35 : restingY - outerDrop + (selected ? 0.43 : 0),
      -distance + index * 0.012 + (selected ? 0.54 : 0),
    ],
    rotation: [0, 0, selected ? 0 : normalized * -0.115],
    scale: baseScale * (selected ? 1.31 : 1),
    renderOrder: selected ? 1_000 : 100 + index,
  };
}

export function screenPointToCameraPlane({
  clientX,
  clientY,
  viewportWidth,
  viewportHeight,
  distance,
  fovDegrees,
}: CameraPlanePointInput): readonly [number, number, number] {
  const safeWidth = Math.max(1, viewportWidth);
  const safeHeight = Math.max(1, viewportHeight);
  const halfHeight = Math.tan((fovDegrees * Math.PI) / 360) * distance;
  const halfWidth = halfHeight * (safeWidth / safeHeight);
  const normalizedX = (clientX / safeWidth) * 2 - 1;
  const normalizedY = 1 - (clientY / safeHeight) * 2;
  return [normalizedX * halfWidth, normalizedY * halfHeight, -distance];
}
